import React from 'react';
import { Box, Typography, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Switch, Divider } from '@mui/material';
import ViewDay from '@mui/icons-material/ViewDay';
import ViewWeek from '@mui/icons-material/ViewWeek';
import ViewModule from '@mui/icons-material/ViewModule';
import WeekendIcon from '@mui/icons-material/Weekend';
import TextFieldsIcon from '@mui/icons-material/TextFields';

import EventIcon from '@mui/icons-material/Event';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import LinkIcon from '@mui/icons-material/Link';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BlockIcon from '@mui/icons-material/Block';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import SearchIcon from '@mui/icons-material/Search';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { toast } from 'react-toastify';
import BottomSheet from '../../../components/mobile/BottomSheet';
import { buildApiUrl } from '../../../utils/apiClient';
import { useAppNavigation } from '../../../hooks/useAppNavigation';

/**
 * MobileCalendarDrawer - Google Calendar-style drawer for mobile calendar options
 * 
 * Opens from a hamburger/menu button and contains:
 * - View mode selection (Day, Week, Month)
 * - Provider selection (checkboxes for multi-provider mode)
 * - Calendar display options (hide weekends, show initials, color mode, legend)
 */
export function MobileCalendarDrawer({
  isOpen,
  onClose,
  viewMode,
  setViewMode,
  hideWeekends,
  setHideWeekends,
  showInitialsOnly,
  setShowInitialsOnly,
  providers = [],
  selectedProviderIds = [],
  onProviderToggle,
  // New props for additional options
  showPersonalCalendar = false,
  setShowPersonalCalendar,
  googleCalendarConnected = false,
  icalFeedUrl = null,
  showResourceLanes = false,
  setShowResourceLanes,
  availableResources = [],
  roomViewMode = false,
  setRoomViewMode,
  selectedRooms = [],
  handleRoomToggle,
  handleSelectAllRooms,
  handleDeselectAllRooms,
  // Clinical action callbacks (optional — Layout.tsx renders without them)
  onBulkComplete = undefined,
  onAiNotesClick = undefined,
  onFindOpening = undefined,
  todaysNeedingAttendance = 0,
  hideCancelled = false,
  setHideCancelled,
  // Relocated toolbar items (mobile toolbar is too narrow for them)
  onNavigateToday = undefined,
  timezoneAbbrev = undefined,
  timezoneOptions = undefined,
  displayTimezone = undefined,
  onTimezoneChange = undefined,
}) {
  const nav = useAppNavigation();
  const viewModes = [
    { value: 'day', label: 'Day', icon: <ViewDay /> },
    { value: 'week', label: 'Week', icon: <ViewWeek /> },
    { value: 'month', label: 'Month', icon: <ViewModule /> }
  ];

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    onClose();
  };

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title="Calendar"
      maxHeight="70vh"
    >
      <Box sx={{ pb: 2 }}>
        {/* View Mode Selection */}
        <List disablePadding>
          {viewModes.map((mode) => (
            <ListItem key={mode.value} disablePadding>
              <ListItemButton
                onClick={() => handleViewModeChange(mode.value)}
                sx={{
                  py: 1.5,
                  px: 3,
                  backgroundColor: viewMode === mode.value ? 'primary.light' : 'transparent',
                  borderRadius: viewMode === mode.value ? '0 24px 24px 0' : 0,
                  mr: viewMode === mode.value ? 2 : 0,
                  '&:hover': {
                    backgroundColor: viewMode === mode.value ? 'primary.light' : 'action.hover'
                  }
                }}
              >
                <ListItemIcon sx={{ 
                  minWidth: 40,
                  color: viewMode === mode.value ? 'primary.main' : 'text.secondary'
                }}>
                  {mode.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={mode.label}
                  primaryTypographyProps={{
                    fontWeight: viewMode === mode.value ? 600 : 400,
                    color: viewMode === mode.value ? 'primary.main' : 'text.primary'
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        {(onNavigateToday || (timezoneOptions?.length ?? 0) > 0) && <Divider sx={{ my: 2 }} />}

        {onNavigateToday && (
          <List disablePadding>
            <ListItem disablePadding>
              <ListItemButton onClick={() => { onNavigateToday(); onClose(); }} sx={{ py: 1.5, px: 3 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <EventIcon color="action" />
                </ListItemIcon>
                <ListItemText primary="Go to Today" />
              </ListItemButton>
            </ListItem>
          </List>
        )}

        {(timezoneOptions?.length ?? 0) > 0 && (
          <>
            <Typography variant="overline" sx={{ px: 3, color: 'text.secondary', display: 'block', mb: 1 }}>
              Timezone{timezoneAbbrev ? ` (${timezoneAbbrev})` : ''}
            </Typography>
            <List disablePadding>
              {timezoneOptions.map(opt => (
                <ListItem key={opt.tz} disablePadding>
                  <ListItemButton
                    onClick={() => { onTimezoneChange?.(opt.tz); onClose(); }}
                    sx={{ py: 1, px: 3 }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <ScheduleIcon color={displayTimezone === opt.tz ? 'primary' : 'action'} />
                    </ListItemIcon>
                    <ListItemText
                      primary={opt.label}
                      primaryTypographyProps={{
                        fontWeight: displayTimezone === opt.tz ? 600 : 400,
                        color: displayTimezone === opt.tz ? 'primary.main' : 'text.primary',
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Provider Selection - only show if providers available */}
        {providers.length > 0 && (
          <>
            <Typography variant="overline" sx={{ px: 3, color: 'text.secondary', display: 'block', mb: 1 }}>
              Providers
            </Typography>
            
            {/* Provider list - click to toggle */}
            <Box sx={{ px: 3, py: 1, maxHeight: 200, overflowY: 'auto' }}>
              {providers.map((provider) => {
                const isActive = selectedProviderIds.includes(provider.id);
                return (
                  <Box
                    key={provider.id}
                    onClick={() => onProviderToggle?.(provider.id)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      py: 0.75,
                      px: 0.5,
                      cursor: 'pointer',
                      borderRadius: '6px',
                      '&:hover': { backgroundColor: 'action.hover' }
                    }}
                  >
                    <Box sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      backgroundColor: isActive ? 'primary.main' : '#d0d0d0',
                      transition: 'background-color 0.2s'
                    }} />
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '0.85rem',
                        color: isActive ? 'text.primary' : '#bdbdbd',
                        fontWeight: isActive ? 500 : 400
                      }}
                    >
                      {provider.name || `${provider.first_name || ''} ${provider.last_name || ''}`.trim() || 'Provider'}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            <Divider sx={{ my: 2 }} />
          </>
        )}

        {/* Display Options */}
        <Typography variant="overline" sx={{ px: 3, color: 'text.secondary', display: 'block', mb: 1 }}>
          Display Options
        </Typography>
        
        <List disablePadding>
          {/* Hide Weekends */}
          <ListItem sx={{ py: 1, px: 3 }}>
            <ListItemIcon sx={{ minWidth: 40 }}>
              <WeekendIcon color="action" />
            </ListItemIcon>
            <ListItemText primary="Hide weekends" />
            <Switch
              edge="end"
              checked={hideWeekends}
              onChange={(e) => setHideWeekends(e.target.checked)}
              size="small"
            />
          </ListItem>

          {/* Show Initials Only */}
          <ListItem sx={{ py: 1, px: 3 }}>
            <ListItemIcon sx={{ minWidth: 40 }}>
              <TextFieldsIcon color="action" />
            </ListItemIcon>
            <ListItemText 
              primary="Show initials only" 
              secondary="Display client initials instead of full names"
            />
            <Switch
              edge="end"
              checked={showInitialsOnly}
              onChange={(e) => setShowInitialsOnly(e.target.checked)}
              size="small"
            />
          </ListItem>

          {/* Color mode is now always doc status — appt type toggle removed */}

          {/* Show Personal Calendar */}
          <ListItem sx={{ py: 1, px: 3 }}>
            <ListItemIcon sx={{ minWidth: 40 }}>
              <EventIcon color="action" />
            </ListItemIcon>
            <ListItemText 
              primary="Personal calendar" 
              secondary="Show events from your personal calendar"
            />
            <Switch
              edge="end"
              checked={showPersonalCalendar}
              onChange={(e) => setShowPersonalCalendar?.(e.target.checked)}
              size="small"
            />
          </ListItem>

          {/* Hide cancelled / no-shows */}
          {setHideCancelled && (
            <ListItem sx={{ py: 1, px: 3 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <BlockIcon color="action" />
              </ListItemIcon>
              <ListItemText 
                primary="Hide cancelled / no-shows" 
                secondary="Filter out cancelled and missed appointments"
              />
              <Switch
                edge="end"
                checked={hideCancelled}
                onChange={(e) => setHideCancelled(e.target.checked)}
                size="small"
              />
            </ListItem>
          )}

          {/* Show Resource Lanes - available for day and week view */}
          {availableResources.length > 0 && (viewMode === 'week' || viewMode === 'day') && (
            <ListItem sx={{ py: 1, px: 3 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <MeetingRoomIcon color="action" />
              </ListItemIcon>
              <ListItemText 
                primary="Show resources" 
                secondary="Display room and equipment lanes"
              />
              <Switch
                edge="end"
                checked={showResourceLanes}
                onChange={(e) => setShowResourceLanes?.(e.target.checked)}
                size="small"
              />
            </ListItem>
          )}

          {/* Room View toggle */}
          {(viewMode === 'week' || viewMode === 'day') && setRoomViewMode && (
            <ListItem sx={{ py: 1, px: 3 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <MeetingRoomIcon color="action" />
              </ListItemIcon>
              <ListItemText 
                primary="Room View" 
                secondary="Show rooms as columns with all providers"
              />
              <Switch
                edge="end"
                checked={roomViewMode}
                onChange={(e) => setRoomViewMode?.(e.target.checked)}
                size="small"
              />
            </ListItem>
          )}
        </List>

        {/* Room selector (when Room View is active) */}
        {roomViewMode && (viewMode === 'day' || viewMode === 'week') && (() => {
          const roomResources = (availableResources || []).filter(r => r.type === 'room');
          if (roomResources.length === 0) return null;
          const allSelected = roomResources.every(r => selectedRooms.includes(r.resource_id));
          return (
            <>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', px: 3, mb: 1 }}>
                <Typography variant="overline" sx={{ color: 'text.secondary' }}>Rooms</Typography>
                <Typography
                  variant="caption"
                  onClick={() => allSelected ? handleDeselectAllRooms?.() : handleSelectAllRooms?.(roomResources.map(r => r.resource_id))}
                  sx={{ fontSize: '0.75rem', color: 'primary.main', cursor: 'pointer', ml: 'auto', '&:hover': { textDecoration: 'underline' } }}
                >
                  {allSelected ? 'Deselect all' : 'Select all'}
                </Typography>
              </Box>
              <Box sx={{ px: 3, py: 1, maxHeight: 200, overflowY: 'auto' }}>
                {roomResources.map((room) => {
                  const isActive = selectedRooms.includes(room.resource_id);
                  return (
                    <Box
                      key={room.id}
                      onClick={() => handleRoomToggle?.(room.resource_id)}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1,
                        py: 0.75, px: 0.5, cursor: 'pointer', borderRadius: '6px',
                        minHeight: 32,
                        '&:hover': { backgroundColor: 'action.hover' }
                      }}
                    >
                      <Box sx={{ width: 12, height: 12, borderRadius: '3px', backgroundColor: isActive ? 'primary.main' : '#d0d0d0', transition: 'background-color 0.2s' }} />
                      <Typography variant="body2" sx={{ fontSize: '0.85rem', color: isActive ? 'text.primary' : '#bdbdbd', fontWeight: isActive ? 500 : 400 }}>
                        {room.name}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </>
          );
        })()}

        <Divider sx={{ my: 2 }} />

        {/* Clinical Actions — mobile-only since ClinicalStatusBar is desktop-only */}
        {(onBulkComplete || onAiNotesClick || onFindOpening) && (
          <>
            <Typography variant="overline" sx={{ px: 3, color: 'text.secondary', display: 'block', mb: 1 }}>
              Clinical Actions
            </Typography>
            <List disablePadding>
              {onBulkComplete && (
                <ListItem disablePadding>
                  <ListItemButton onClick={() => { onClose(); onBulkComplete(); }} sx={{ py: 1, px: 3 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <PlaylistAddCheckIcon color="action" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Confirm Attendance"
                      secondary={todaysNeedingAttendance > 0 ? `${todaysNeedingAttendance} need confirmation` : 'All confirmed'}
                    />
                  </ListItemButton>
                </ListItem>
              )}
              {onAiNotesClick && (
                <ListItem disablePadding>
                  <ListItemButton onClick={() => { onClose(); onAiNotesClick(); }} sx={{ py: 1, px: 3 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <EditNoteIcon color="action" />
                    </ListItemIcon>
                    <ListItemText primary="AI Notes" secondary="Write notes with AI" />
                  </ListItemButton>
                </ListItem>
              )}
              {onFindOpening && (
                <ListItem disablePadding>
                  <ListItemButton onClick={() => { onClose(); onFindOpening(); }} sx={{ py: 1, px: 3 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <SearchIcon color="action" />
                    </ListItemIcon>
                    <ListItemText primary="Find Opening" secondary="Search for available slots" />
                  </ListItemButton>
                </ListItem>
              )}
            </List>
            <Divider sx={{ my: 2 }} />
          </>
        )}

        {/* Quick Links */}
        <Typography variant="overline" sx={{ px: 3, color: 'text.secondary', display: 'block', mb: 1 }}>
          Quick Links
        </Typography>
        
        <List disablePadding>
          {/* Connect Google Calendar */}
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => window.location.href = buildApiUrl('/api/auth/google-calendar/connect')}
              sx={{ py: 1, px: 3 }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <LinkIcon color="action" />
              </ListItemIcon>
              <ListItemText 
                primary="Connect Google Calendar" 
                secondary={googleCalendarConnected ? "Connected" : "Link your personal calendar"}
              />
            </ListItemButton>
          </ListItem>

          {/* Sync Out — iCal subscription feed */}
          {icalFeedUrl && (
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  navigator.clipboard.writeText(icalFeedUrl)
                    .then(() => toast.success('iCal feed URL copied! Paste it into Google Calendar > Add by URL'))
                    .catch(() => toast.error('Failed to copy URL to clipboard'));
                }}
                sx={{ py: 1, px: 3 }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <ContentCopyIcon color="action" />
                </ListItemIcon>
                <ListItemText 
                  primary="Sync Out" 
                  secondary="Copy iCal URL to add your work schedule to Google Calendar"
                />
              </ListItemButton>
            </ListItem>
          )}

          {/* Work Schedule */}
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => nav.toSettings('work-schedule')}
              sx={{ py: 1, px: 3 }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <ScheduleIcon color="action" />
              </ListItemIcon>
              <ListItemText 
                primary="Work schedule" 
                secondary="Set your availability hours"
              />
            </ListItemButton>
          </ListItem>
        </List>
      </Box>
    </BottomSheet>
  );
}
